import { useMemo, useRef, useState } from 'react';
import * as act from '../app/actions';
import { closeDialog } from '../app/dialogs';
import type { Model } from '../data/model';
import { newSession } from '../data/maps';
import { isShortLink, pinLabel, readPin, shortCode } from '../data/pins';
import { EmptyHelp, nearly } from './bits';
import { firstPart, LookupRows, placeOf, recentPlaces, useLookup, type LookedPlace, type Place } from './Lookup';
import { toast } from './Overlays';
import { GOOGLE_MS, lastDay } from '../data/waiting';
import { dayOf } from '@day';
import { Icon } from './Icon';

/**
 * A stop typed in for the waiting list (docs/waiting-stops.md): its address found as it's typed (a customer's pin with no
 * lookup; a place from the business's routes; else a lookup once typing pauses), its customer, and the day it's for if
 * there is one.
 */
export function AddWaiting({ m }: { m: Model }) {
  const [q, setQ] = useState('');
  /** The place picked from the rows, for what was typed when it was picked. */
  const [chosen, setChosen] = useState<(Place & { q: string }) | null>(null);
  const [placing, setPlacing] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [day, setDay] = useState('');
  const session = useRef(newSession());
  // Only places with an ID: one left over a day is found again by it when it's planned.
  const places = useMemo(() => recentPlaces(m).filter((p) => p.placeId), [m.data.history, m.data.routes, m.today, m.zone]);
  const text = q.trim();
  const ql = text.toLowerCase();
  const pin = text ? readPin(text, m.shop) : null;
  const short = isShortLink(q);
  const picked = !pin && chosen?.q === text ? chosen : null;
  const hits = !pin && !picked && ql.length >= 2 ? places.filter((p) => p.address.toLowerCase().includes(ql)).slice(0, 5) : [];
  const found = useLookup(text, !picked && text.length >= 4 && !pin && !short && !hits.length, m.business?.country?.toLowerCase(), session, m.shop);
  const look = found && found !== 'looking' && found.q === text ? found : null;
  // Geocoding's one place is taken as it is, and shown; the place search's suggestions wait to be picked.
  const place = picked ?? (!pin && look?.via === 'geocode' ? look.places[0] : null);
  const rough = place?.precision === 'low';
  const ready = !!pin || !!place;
  const pick = async (p: LookedPlace) => {
    setPlacing(true);
    const got = await placeOf(p, session.current);
    setPlacing(false);
    if (typeof got === 'string') return toast(got);
    session.current = newSession();
    setChosen({ ...got, q: text });
  };
  const add = () => {
    const at = Date.now();
    const id = crypto.randomUUID();
    const stop = pin
      ? act.storedStop({ id, address: pinLabel(pin), lat: pin.lat, lng: pin.lng })
      : act.storedStop({
          id,
          address: place!.address || text,
          // What was typed is the owner's own wording, which may leave Routr; Google's stays inside it. Only for a lookup:
          // a few letters that found a place from the routes aren't an address.
          writtenAddress: place!.google && text.length >= 6 ? text : null,
          placeId: place!.placeId ?? null,
          houseNumber: place!.houseNumber ?? null,
          lat: null as unknown as number,
          lng: null as unknown as number,
          precision: rough ? 'low' : 'exact',
        });
    // A place from the routes has only the days Google's position has left there.
    const until = place?.googleUntil ?? at + GOOGLE_MS;
    act.addWaiting(
      [
        {
          stop: { ...stop, id, address: String(stop.address), recipientName: name.trim() || null, recipientPhone: phone.trim() || null, notes: note.trim() || null },
          source: 'typed',
          day: day || null,
          google: place && until > at ? { lat: place.lat, lng: place.lng, at: until - GOOGLE_MS } : null,
        },
      ],
      `${pin ? 'The pin' : firstPart(place!.address || text)} is waiting for a route${day ? ` · ${act.dayName(m, day)}` : ''}`,
    );
    closeDialog();
  };
  return (
    <>
      <div className="title">Add a stop to Waiting</div>
      <div className="cap" style={{ marginTop: 4 }}>
        It waits here, with its customer, until you plan its route. Kept 28 days.
      </div>
      <label className="field" style={{ marginTop: 14 }}>
        <Icon name="magnifyingGlass" />
        <input
          data-first
          placeholder="Address, Maps link, plus code or coordinates"
          value={q}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (ready) return add();
            if (hits[0]) return setChosen({ ...hits[0], q: text });
            if (look?.places[0]) void pick(look.places[0]);
          }}
          aria-label="The stop’s address"
        />
      </label>
      {text && (
        <div className="rows" style={{ marginTop: 6 }}>
          {pin ? (
            <div className="item hot">
              <Icon name="mapPinFill" />
              <div className="grow">
                <b>{pinLabel(pin)}</b>
                <div className="cap">The customer’s own pin · no lookup · kept with the stop</div>
              </div>
            </div>
          ) : short ? (
            <div className="empty cap">That’s a short Maps link, which only opens in a browser: open it, then paste the full address bar or the coordinates here.</div>
          ) : place ? (
            <div className="item hot">
              <Icon name="mapPin" />
              <div className="grow">
                <b>{firstPart(place.address)}</b>
                <div className="cap">
                  {place.address.split(',').slice(1, 3).join(',').trim()} · {!place.google ? 'from your routes' : rough ? 'only roughly: the courier may need to call' : 'found'} · looked up again when it’s planned
                </div>
              </div>
              <Icon name="check" />
            </div>
          ) : (
            <>
              {hits.map((p, i) => (
                <div key={p.address} className={`item${i === 0 ? ' hot' : ''}`} role="button" tabIndex={0} onClick={() => setChosen({ ...p, q: text })}>
                  <Icon name="mapPin" />
                  <div className="grow">
                    <b>{firstPart(p.address)}</b>
                    <div className="cap">{p.area} · from your routes</div>
                  </div>
                  {i === 0 ? <span className="kbd">↵</span> : <Icon name="plus" className="ink3" />}
                </div>
              ))}
              <LookupRows text={text} found={found} placing={placing} onPick={(p) => void pick(p)} />
              {look && !look.places.length && (
                <EmptyHelp
                  q={look.error ? undefined : look.q}
                  title={look.error}
                  tries={[
                    ...nearly(look.q, places, (p) => p.address).map((p) => ({ icon: 'mapPin', label: firstPart(p.address), note: `${p.area} · from your routes`, act: `place:${p.address}` })),
                    { icon: 'clipboardText', label: 'Paste the customer’s pin', note: 'A Google Maps link, a plus code or coordinates: no lookup', act: 'clear' },
                    { icon: 'notePencil', label: 'Write it another way', note: 'The street and the area, without the landmark', act: 'focus' },
                  ]}
                  onTry={(a) => {
                    const near = a.startsWith('place:') ? places.find((x) => x.address === a.slice(6)) : null;
                    if (near) setChosen({ ...near, q: text });
                    else if (a === 'clear') setQ('');
                    (document.querySelector('#modal [data-first]') as HTMLInputElement | null)?.focus();
                  }}
                />
              )}
            </>
          )}
        </div>
      )}
      <div className="planedit" style={{ marginTop: 12 }}>
        <input placeholder="Customer’s name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" autoCorrect="off" spellCheck={false} aria-label="Customer’s name" />
        <input placeholder="Their phone" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="off" aria-label="Their phone" />
        <input placeholder="A note for the courier" value={note} onChange={(e) => setNote(e.target.value)} autoComplete="off" aria-label="A note for the courier" />
      </div>
      <label className="row" style={{ marginTop: 12, gap: 8 }}>
        <span className="cap" style={{ width: 92 }}>
          The day it’s for
        </span>
        <input type="date" className="dateinput" value={day} min={m.today} max={lastDay(m.today)} onChange={(e) => setDay(e.target.value)} aria-label="The day it’s for (optional)" />
        {day ? (
          <button className="link cap" onClick={() => setDay('')}>
            Any day
          </button>
        ) : (
          <span className="cap">Optional</span>
        )}
      </label>
      <div className="row" style={{ marginTop: 18, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={closeDialog}>
          Cancel
        </button>
        <button className="btn primary" disabled={!ready} onClick={add}>
          <Icon name="plus" />
          Add to Waiting
        </button>
      </div>
      {pin && <div className="cap" style={{ marginTop: 8, textAlign: 'right' }}>{shortCode(pin.lat, pin.lng)}</div>}
    </>
  );
}

/** A day further out than the menu's week. */
export function WaitingDay({ m, ids }: { m: Model; ids: string[] }) {
  const [day, setDay] = useState('');
  // Not past the day before the first of them goes (its 28 days): it would be deleted before its day.
  const ends = m.waiting.filter((w) => ids.includes(w.id) && w.expiresAt != null).map((w) => dayOf(w.expiresAt! - 864e5, m.zone));
  const max = ends.length ? ends.sort()[0] : lastDay(m.today);
  return (
    <>
      <div className="title">The day {ids.length === 1 ? 'it’s' : 'they’re'} for</div>
      <input type="date" data-first className="dateinput" style={{ marginTop: 14 }} value={day} min={m.today} max={max} onChange={(e) => setDay(e.target.value)} aria-label="The day" />
      <div className="cap" style={{ marginTop: 6 }}>Up to {max}: waiting stops are kept 28 days from when they were added.</div>
      <div className="row" style={{ marginTop: 18, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={closeDialog}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={!/^\d{4}-\d{2}-\d{2}$/.test(day) || day > max}
          onClick={() => {
            act.setWaitingDay(ids, day);
            closeDialog();
          }}
        >
          Set the day
        </button>
      </div>
    </>
  );
}
